import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

export interface ThreadOrderEvent {
  isReply?: boolean;
  smartOrderUUID: string;
  threadId: string;
}

type ThreadOrderCallback = (event: ThreadOrderEvent) => void;

interface ThreadOrderEventsContextValue {
  emitThreadOrderProcessed: (event: ThreadOrderEvent) => void;
  emitThreadOrderProcessing: (event: ThreadOrderEvent) => void;
  onThreadOrderProcessed: (cb: ThreadOrderCallback) => () => void;
  onThreadOrderProcessing: (cb: ThreadOrderCallback) => () => void;
}

const ThreadOrderEventsContext =
  createContext<ThreadOrderEventsContextValue | null>(null);

interface Props {
  children?: React.ReactNode;
}

export const ThreadOrderEventsProvider: React.FC<Props> = ({ children }) => {
  const processingCallbacksRef = useRef<Set<ThreadOrderCallback>>(new Set());
  const processedCallbacksRef = useRef<Set<ThreadOrderCallback>>(new Set());

  const onThreadOrderProcessing = useCallback((cb: ThreadOrderCallback) => {
    processingCallbacksRef.current.add(cb);
    return () => {
      processingCallbacksRef.current.delete(cb);
    };
  }, []);

  const onThreadOrderProcessed = useCallback((cb: ThreadOrderCallback) => {
    processedCallbacksRef.current.add(cb);
    return () => {
      processedCallbacksRef.current.delete(cb);
    };
  }, []);

  const emitThreadOrderProcessing = useCallback((event: ThreadOrderEvent) => {
    processingCallbacksRef.current.forEach((cb) => cb(event));
  }, []);

  const emitThreadOrderProcessed = useCallback((event: ThreadOrderEvent) => {
    processedCallbacksRef.current.forEach((cb) => cb(event));
  }, []);

  // we probably don't need to memoize this, but we do this for other contexts in our codebase so we'll just follow
  // suit and do the same here for consistency
  const ctxValue: ThreadOrderEventsContextValue = useMemo(
    () => ({
      emitThreadOrderProcessed,
      emitThreadOrderProcessing,
      onThreadOrderProcessed,
      onThreadOrderProcessing,
    }),
    [
      emitThreadOrderProcessed,
      emitThreadOrderProcessing,
      onThreadOrderProcessed,
      onThreadOrderProcessing,
    ],
  );

  return (
    <ThreadOrderEventsContext.Provider value={ctxValue}>
      {children}
    </ThreadOrderEventsContext.Provider>
  );
};

export const useThreadOrderEventsContext = () => {
  const context = useContext(ThreadOrderEventsContext);
  if (!context) {
    throw new Error(
      'useThreadOrderEventsContext requires a ThreadOrderEventsProvider',
    );
  }
  return context;
};
